import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Loader2, Sparkles, Wand2, Save, Send, CheckCircle2, Shuffle, Gauge, Tags,
} from "lucide-react";
import { toast } from "sonner";
import { listCurricula, listClasses, listSubjects, listTopics } from "@/lib/curriculum.functions";
import {
  generateQuestionSet, saveGeneratedQuestions, publishGeneratedQuestions,
  type GeneratedQuestion,
} from "@/lib/question-generator.functions";

export const Route = createFileRoute("/teacher/question-generator")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({
    meta: [
      { title: "AI Question Generator · MAKAI Teachers" },
      { name: "description", content: "Generate curriculum-tagged questions with answers, marking guides and alternative versions, then publish to homework, quizzes, assessments, Makai Live or the Exam Builder." },
      { property: "og:title", content: "AI Question Generator · MAKAI Teachers" },
      { property: "og:description", content: "Curriculum-aligned AI question generation with marking guides, variants and one-click publishing." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Page,
});

const TYPES = [
  { id: "multiple_choice", label: "Multiple choice" },
  { id: "true_false", label: "True / False" },
  { id: "matching", label: "Matching" },
  { id: "fill_blank", label: "Fill in blanks" },
  { id: "short_answer", label: "Short answer" },
  { id: "essay", label: "Essay" },
  { id: "practical", label: "Practical" },
  { id: "diagram_label", label: "Diagram" },
  { id: "maths_working", label: "Maths working" },
] as const;

const BLOOMS = ["remember", "understand", "apply", "analyze", "evaluate", "create"] as const;
const DIFFS = ["easy", "medium", "hard", "expert", "mixed"] as const;

function Page() {
  const ctx = useTeacherContext();

  /* ------------- curriculum pickers ------------- */
  const curricula = useQuery({ queryKey: ["qg-cur"], queryFn: () => listCurricula() });
  const [curId, setCurId] = useState("");
  const cur = curId || curricula.data?.[0]?.id || "";
  const curName = curricula.data?.find((c) => c.id === cur)?.name ?? "Uganda NCDC";

  const classes = useQuery({
    queryKey: ["qg-cls", cur], enabled: !!cur,
    queryFn: () => listClasses({ data: { curriculumId: cur } }),
  });
  const subjects = useQuery({
    queryKey: ["qg-sub", cur], enabled: !!cur,
    queryFn: () => listSubjects({ data: { curriculumId: cur } }),
  });
  const [classId, setClassId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const topics = useQuery({
    queryKey: ["qg-top", subjectId, classId], enabled: !!subjectId && !!classId,
    queryFn: () => listTopics({ data: { subjectId, classId } }),
  });
  const [topicId, setTopicId] = useState("");

  const className = classes.data?.find((c) => c.id === classId)?.name ?? "";
  const subjectName = subjects.data?.find((s) => s.id === subjectId)?.name ?? "";
  const topicName = topics.data?.find((t) => t.id === topicId)?.name ?? "";

  /* ------------- generation controls ------------- */
  const [difficulty, setDifficulty] = useState<string>("medium");
  const [types, setTypes] = useState<string[]>(["multiple_choice", "short_answer"]);
  const [blooms, setBlooms] = useState<string[]>(["remember", "understand", "apply"]);
  const [marks, setMarks] = useState(2);
  const [count, setCount] = useState(10);
  const [variants, setVariants] = useState(1);
  const [randomise, setRandomise] = useState(true);
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");

  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<GeneratedQuestion[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const gen = useServerFn(generateQuestionSet);
  const saveFn = useServerFn(saveGeneratedQuestions);

  const chosen = useMemo(
    () => items.filter((_, i) => selected.has(i)),
    [items, selected],
  );

  const toggle = (arr: string[], set: (v: string[]) => void, id: string) =>
    set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  async function run() {
    if (!className || !subjectName) {
      toast.error("Choose a class and a subject first.");
      return;
    }
    if (!types.length) { toast.error("Pick at least one question type."); return; }
    setBusy(true);
    const t = toast.loading("MAKAI is writing your questions…");
    try {
      const out = await gen({
        data: {
          curriculum: curName,
          className,
          subject: subjectName,
          topic: topicName || undefined,
          difficulty: difficulty as never,
          types,
          marksPerQuestion: marks,
          bloomLevels: blooms.length ? blooms : ["understand"],
          count,
          variantsPerQuestion: variants,
          randomiseNumbers: randomise,
          learningOutcome: outcome || undefined,
          notes: notes || undefined,
        },
      });
      setItems(out.questions);
      setSelected(new Set(out.questions.map((_, i) => i)));
      toast.success(`${out.questions.length} questions ready`, { id: t });
    } catch (e) {
      toast.error((e as Error).message ?? "Generation failed", { id: t });
    } finally {
      setBusy(false);
    }
  }

  async function saveBank(status: "draft" | "approved") {
    if (!chosen.length) { toast.error("Select at least one question."); return; }
    const t = toast.loading("Saving to question bank…");
    try {
      const r = await saveFn({
        data: {
          schoolId: ctx.schoolId ?? null,
          classId: classId || undefined,
          subjectId: subjectId || undefined,
          topicId: topicId || undefined,
          status,
          questions: chosen as never,
        },
      });
      toast.success(`Saved ${r.inserted}· duplicates ${r.duplicates}· failed ${r.failed}`, { id: t });
    } catch (e) {
      toast.error((e as Error).message, { id: t });
    }
  }

  return (
    <AppShell
      role="teacher"
      title="AI Question Generator"
      subtitle="Curriculum-tagged questions with answers, marking guides and variants"
    >
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        {/* -------- controls -------- */}
        <Card className="h-fit lg:sticky lg:top-24">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Wand2 className="h-4 w-4 text-primary" /> Question brief
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5 col-span-2">
                <Label>Curriculum</Label>
                <Select value={cur} onValueChange={setCurId}>
                  <SelectTrigger><SelectValue placeholder="Curriculum" /></SelectTrigger>
                  <SelectContent>
                    {curricula.data?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Class</Label>
                <Select value={classId} onValueChange={setClassId}>
                  <SelectTrigger><SelectValue placeholder="Class" /></SelectTrigger>
                  <SelectContent>
                    {classes.data?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Subject</Label>
                <Select value={subjectId} onValueChange={setSubjectId}>
                  <SelectTrigger><SelectValue placeholder="Subject" /></SelectTrigger>
                  <SelectContent>
                    {subjects.data?.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Topic</Label>
                <Select value={topicId} onValueChange={setTopicId}>
                  <SelectTrigger><SelectValue placeholder={topics.data?.length ? "Topic" : "Pick class + subject first"} /></SelectTrigger>
                  <SelectContent>
                    {topics.data?.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Difficulty</Label>
                <Select value={difficulty} onValueChange={setDifficulty}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DIFFS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Marks each</Label>
                <Input type="number" min={1} max={50} value={marks}
                  onChange={(e) => setMarks(Math.max(1, Number(e.target.value) || 1))} />
              </div>
              <div className="space-y-1.5">
                <Label>Questions</Label>
                <Input type="number" min={1} max={30} value={count}
                  onChange={(e) => setCount(Math.min(30, Math.max(1, Number(e.target.value) || 1)))} />
              </div>
              <div className="space-y-1.5">
                <Label>Alt versions</Label>
                <Input type="number" min={0} max={4} value={variants}
                  onChange={(e) => setVariants(Math.min(4, Math.max(0, Number(e.target.value) || 0)))} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Question types</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {TYPES.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={types.includes(t.id)} onCheckedChange={() => toggle(types, setTypes, t.id)} />
                    {t.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Bloom's taxonomy levels</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {BLOOMS.map((b) => (
                  <label key={b} className="flex items-center gap-2 text-sm capitalize">
                    <Checkbox checked={blooms.includes(b)} onCheckedChange={() => toggle(blooms, setBlooms, b)} />
                    {b}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="flex items-center gap-2 text-sm">
                <Shuffle className="h-4 w-4 text-muted-foreground" /> Randomise numbers in variants
              </div>
              <Switch checked={randomise} onCheckedChange={setRandomise} />
            </div>

            <div className="space-y-1.5">
              <Label>Learning outcome (optional)</Label>
              <Input value={outcome} onChange={(e) => setOutcome(e.target.value)}
                placeholder="e.g. Solve problems involving fractions" />
            </div>
            <div className="space-y-1.5">
              <Label>Notes for MAKAI</Label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. UNEB PLE style, use Ugandan market examples." />
            </div>

            <Button className="w-full" onClick={run} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Generate questions
            </Button>
          </CardContent>
        </Card>

        {/* -------- results -------- */}
        <div className="space-y-4">
          {!items.length ? (
            <Card>
              <CardContent className="py-16 text-center text-sm text-muted-foreground">
                Set your brief on the left and MAKAI will write questions with answers,
                explanations, marking guides, alternative versions and difficulty analysis.
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardContent className="flex flex-wrap items-center gap-2 py-3">
                  <Badge variant="outline">{items.length} generated</Badge>
                  <Badge variant="secondary">{chosen.length} selected</Badge>
                  <Badge variant="outline">
                    {chosen.reduce((s, q) => s + (q.marks ?? 1), 0)} marks
                  </Badge>
                  <div className="ml-auto flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => saveBank("draft")}>
                      <Save className="mr-1.5 h-4 w-4" />Save to bank
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => saveBank("approved")}>
                      <CheckCircle2 className="mr-1.5 h-4 w-4" />Save & approve
                    </Button>
                    <PublishDialog
                      questions={chosen}
                      defaults={{
                        schoolId: ctx.schoolId,
                        subjectId,
                        title: `${subjectName || "Questions"}${topicName ? " · " + topicName : ""}`,
                        difficulty: difficulty === "mixed" || difficulty === "expert" ? "hard" : (difficulty as "easy" | "medium" | "hard"),
                      }}
                    />
                  </div>
                </CardContent>
              </Card>

              {items.map((q, i) => (
                <QuestionCard
                  key={i}
                  q={q}
                  index={i}
                  checked={selected.has(i)}
                  onToggle={() => {
                    const next = new Set(selected);
                    next.has(i) ? next.delete(i) : next.add(i);
                    setSelected(next);
                  }}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}

/* ------------------------------------------------------------------ */

function QuestionCard({
  q, index, checked, onToggle,
}: { q: GeneratedQuestion; index: number; checked: boolean; onToggle: () => void }) {
  const tags = q.tags ?? {};
  return (
    <Card className={checked ? "border-primary/40" : ""}>
      <CardHeader className="flex flex-row items-start gap-3 pb-2">
        <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-1" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{index + 1}. {q.stem}</p>
          <div className="mt-2 flex flex-wrap gap-1">
            <Badge variant="secondary">{q.type.replace(/_/g, " ")}</Badge>
            <Badge variant="outline">{q.difficulty ?? "medium"}</Badge>
            <Badge variant="outline">{q.bloom_level ?? "understand"}</Badge>
            <Badge variant="outline">{q.marks ?? 1} marks</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!!q.options?.length && (
          <ul className="space-y-1">
            {q.options.map((o, j) => (
              <li key={j} className={`flex items-start gap-2 ${o.is_correct ? "font-medium text-primary" : ""}`}>
                {o.is_correct ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <span className="w-4" />}
                <span>{o.label}{o.value && o.value !== o.label ? `: ${o.value}` : ""}</span>
              </li>
            ))}
          </ul>
        )}
        {q.correct_answer && (
          <p><span className="text-muted-foreground">Answer: </span>{q.correct_answer}</p>
        )}
        {!!q.working_steps?.length && (
          <div>
            <p className="text-muted-foreground">Working</p>
            <ol className="ml-4 list-decimal">{q.working_steps.map((s, j) => <li key={j}>{s}</li>)}</ol>
          </div>
        )}
        {q.diagram_description && (
          <p><span className="text-muted-foreground">Diagram: </span>{q.diagram_description}</p>
        )}
        {!!q.apparatus?.length && (
          <p><span className="text-muted-foreground">Apparatus: </span>{q.apparatus.join(", ")}</p>
        )}
        {q.explanation && <p className="text-muted-foreground">💡 {q.explanation}</p>}
        {q.marking_guide && (
          <div className="rounded-lg bg-muted/50 p-3">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Marking guide</p>
            <p className="whitespace-pre-wrap">{q.marking_guide}</p>
          </div>
        )}
        {q.difficulty_analysis && (
          <div className="rounded-lg border p-3">
            <p className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <Gauge className="h-3.5 w-3.5" /> Difficulty analysis
              {q.difficulty_analysis.rating ? ` · ${q.difficulty_analysis.rating}/5` : ""}
            </p>
            {q.difficulty_analysis.reason && <p>{q.difficulty_analysis.reason}</p>}
            {!!q.difficulty_analysis.common_mistakes?.length && (
              <p className="mt-1 text-muted-foreground">
                Common mistakes: {q.difficulty_analysis.common_mistakes.join("; ")}
              </p>
            )}
            {!!q.difficulty_analysis.prerequisites?.length && (
              <p className="text-muted-foreground">
                Prerequisites: {q.difficulty_analysis.prerequisites.join("; ")}
              </p>
            )}
          </div>
        )}
        {!!q.variants?.length && (
          <div className="rounded-lg border p-3">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Alternative versions ({q.variants.length})
            </p>
            <ul className="space-y-2">
              {q.variants.map((v, j) => (
                <li key={j}>
                  <p>{v.stem}</p>
                  {v.correct_answer && <p className="text-muted-foreground">Answer: {v.correct_answer}</p>}
                </li>
              ))}
            </ul>
          </div>
        )}
        <Separator />
        <div className="flex flex-wrap items-center gap-1 text-xs">
          <Tags className="h-3.5 w-3.5 text-muted-foreground" />
          {[
            ["Subject", tags.subject], ["Topic", tags.topic], ["Skill", tags.skill],
            ["Concept", tags.concept], ["Bloom", tags.bloom], ["Outcome", tags.learning_outcome],
          ].filter(([, v]) => !!v).map(([k, v]) => (
            <Badge key={k as string} variant="outline" className="font-normal">{k}: {v}</Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */

function PublishDialog({
  questions, defaults,
}: {
  questions: GeneratedQuestion[];
  defaults: { schoolId: string | null; subjectId: string; title: string; difficulty: "easy" | "medium" | "hard" };
}) {
  const ctx = useTeacherContext();
  const publish = useServerFn(publishGeneratedQuestions);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<"homework" | "quiz" | "assessment" | "live" | "exam">("quiz");
  const [title, setTitle] = useState(defaults.title);
  const [streamId, setStreamId] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [timeLimit, setTimeLimit] = useState(30);
  const [liveId, setLiveId] = useState("");
  const [busy, setBusy] = useState(false);

  const lives = useQuery({
    queryKey: ["qg-live", ctx.schoolId], enabled: open && target === "live" && !!ctx.schoolId,
    queryFn: async () => {
      const { data } = await supabase
        .from("live_lessons").select("id, title, scheduled_start")
        .eq("school_id", ctx.schoolId!)
        .order("scheduled_start", { ascending: false }).limit(25);
      return data ?? [];
    },
  });

  async function run() {
    if (!ctx.schoolId) { toast.error("No school linked to your account."); return; }
    if (!questions.length) { toast.error("Select questions first."); return; }
    setBusy(true);
    const t = toast.loading("Publishing…");
    try {
      const r = await publish({
        data: {
          target,
          title: title.trim() || "MAKAI questions",
          schoolId: ctx.schoolId,
          streamId: streamId || undefined,
          subjectId: defaults.subjectId || undefined,
          teacherId: ctx.staffId ?? undefined,
          liveLessonId: liveId || undefined,
          dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
          timeLimitMin: target === "quiz" ? timeLimit : undefined,
          difficulty: defaults.difficulty,
          questions: questions as never,
        },
      });
      toast.success(`Published ${r.count} questions`, { id: t });
      setOpen(false);
    } catch (e) {
      toast.error((e as Error).message, { id: t });
    } finally {
      setBusy(false);
    }
  }

  const needsStream = target === "homework" || target === "assessment";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" disabled={!questions.length}>
          <Send className="mr-1.5 h-4 w-4" />Publish
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Publish {questions.length} questions</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Send to</Label>
            <Select value={target} onValueChange={(v) => setTarget(v as never)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="homework">Homework</SelectItem>
                <SelectItem value="quiz">Quiz</SelectItem>
                <SelectItem value="assessment">Assessment</SelectItem>
                <SelectItem value="live">Makai Live</SelectItem>
                <SelectItem value="exam">Exam Builder pool</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          {needsStream && (
            <div className="space-y-1.5">
              <Label>Class stream</Label>
              <Select value={streamId} onValueChange={setStreamId}>
                <SelectTrigger><SelectValue placeholder="Choose stream" /></SelectTrigger>
                <SelectContent>
                  {ctx.streams.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.class_name} {s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {target === "homework" && (
            <div className="space-y-1.5">
              <Label>Due</Label>
              <Input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
            </div>
          )}
          {target === "quiz" && (
            <div className="space-y-1.5">
              <Label>Time limit (minutes)</Label>
              <Input type="number" min={1} max={300} value={timeLimit}
                onChange={(e) => setTimeLimit(Math.max(1, Number(e.target.value) || 30))} />
            </div>
          )}
          {target === "live" && (
            <div className="space-y-1.5">
              <Label>Live lesson</Label>
              <Select value={liveId} onValueChange={setLiveId}>
                <SelectTrigger><SelectValue placeholder="Choose a live lesson" /></SelectTrigger>
                <SelectContent>
                  {lives.data?.map((l) => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={run} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
